import React, { useContext, useEffect, useRef, useState } from 'react';

import HeaderNavBarContext from '../context/HeaderNavBarContext';
import getFormattedMenuLinks from '../utils/getFormattedMenuLinks';
import { Link, MaskedLink } from '../../../components/Link';
import Box from '../../../components/Box/Box';
import Icon from '../../../components/Icon/Icon';
import { TMenuLink } from '../../../types/TMenuLink';

type TUserDropdownMenu = {
  showHamburgerMenu: () => void;
  colorInverse: boolean;
  menuLinks?: TMenuLink[];
};

const UserDropdownMenu = ({ showHamburgerMenu, menuLinks, colorInverse }: TUserDropdownMenu) => {
  const {
    userDetails: { userName },
    ssoLinks,
  } = useContext<any>(HeaderNavBarContext);
  const [isOpen, setIsOpen] = useState(false);
  const dropDownMenuRef = useRef<HTMLDivElement>(null);

  const handleFocusOut = (event: FocusEvent) => {
    if (dropDownMenuRef.current && !dropDownMenuRef.current.contains(event.relatedTarget as Node)) {
      setIsOpen(false);
    }
  };

  const options = [
    ...getFormattedMenuLinks(menuLinks ?? []),
    {
      value: 'ssoLinks.logout.text',
      label: (
        <MaskedLink href={ssoLinks.logout.url} data-role={`mymp-${ssoLinks.logout.role}`} tabIndex={0}>
          {ssoLinks.logout.text}
        </MaskedLink>
      ),
    },
  ];

  useEffect(() => {
    const dropDownMenuElement = dropDownMenuRef.current;
    if (dropDownMenuElement) {
      dropDownMenuElement.addEventListener('focusout', handleFocusOut, false);
    }

    return () => {
      if (dropDownMenuElement) {
        dropDownMenuElement.removeEventListener('focusout', handleFocusOut, false);
      }
    };
  }, [isOpen]);

  return (
    <>
      <li className="hz-hide-lte-md">
        <Box parent="Nav" name="dropdown" as="div" modifiers={[isOpen ? 'open' : 'closed']} ref={dropDownMenuRef}>
          <Link
            className="hz-Nav-dropdown-toggle"
            tabIndex={0}
            aria-expanded={isOpen}
            onClick={() => setIsOpen(!isOpen)}
            role="button"
            data-role="mymp-username"
          >
            <Icon name="profile" color={colorInverse ? 'background-default-inverse' : 'background-default'} />
            <span className="hz-hide-lte-md">{userName}</span>
            <Icon name="arrow-down" />
          </Link>
          {isOpen && (
            <Box as="ul" parent="Nav-dropdown" name="menu" role="menu">
              {options.map(({ value, label, ...rest }, key) => {
                return (
                  <Box {...rest} value={String(value)} key={key} as="li" parent="Nav" name="pill">
                    {label}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </li>

      <li className="hz-hide-gte-lg">
        <Link role="button" aria-haspopup="true" data-role="hamburgerOpen" tabIndex={-1} onClick={showHamburgerMenu}>
          <Icon name="profile" color={colorInverse ? 'background-default-inverse' : 'background-default'} />
        </Link>
      </li>
    </>
  );
};

export default UserDropdownMenu;
